import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const STATUS_COLOR = {
    open:        { bg: 'rgba(59,130,246,0.12)', color: '#60a5fa', label: 'Open' },
    in_progress: { bg: 'rgba(234,179,8,0.12)',  color: '#fbbf24', label: 'In Progress' },
    resolved:    { bg: 'rgba(34,197,94,0.12)',  color: '#4ade80', label: 'Resolved' },
    closed:      { bg: 'rgba(100,116,139,0.12)',color: '#94a3b8', label: 'Closed' },
};

const s = {
    h1:    { fontSize: 22, fontWeight: 800, color: '#1a1d23', marginBottom: 4 },
    sub:   { color: '#64748b', fontSize: 14, marginBottom: 28 },
    card:  { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 24px', marginBottom: 12, cursor: 'pointer', transition: 'box-shadow .15s' },
    badge: (status) => ({ display:'inline-block', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600, background: STATUS_COLOR[status]?.bg, color: STATUS_COLOR[status]?.color }),
    btn:   { padding:'10px 22px', background:'#1d4ed8', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer' },
    btnSm: { padding:'7px 14px', background:'#1d4ed8', color:'#fff', border:'none', borderRadius:6, fontWeight:600, fontSize:13, cursor:'pointer' },
    inp:   { width:'100%', padding:'10px 12px', border:'1px solid #d1d5db', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' },
    lbl:   { display:'block', fontWeight:600, fontSize:13, color:'#374151', marginBottom:5, marginTop:14 },
    err:   { color:'#ef4444', fontSize:13, marginTop:8 },
    msg:   (isAdmin) => ({
        maxWidth:'80%', padding:'10px 14px', borderRadius:10, fontSize:14, lineHeight:1.55,
        background: isAdmin ? '#1d4ed8' : '#f3f4f6',
        color: isAdmin ? '#fff' : '#1a1d23',
        alignSelf: isAdmin ? 'flex-end' : 'flex-start',
        whiteSpace: 'pre-wrap',
    }),
    msgWrap: { display:'flex', flexDirection:'column', gap:10, marginBottom:20 },
    msgMeta: (isAdmin) => ({ fontSize:11, color:'#94a3b8', marginTop:3, textAlign: isAdmin ? 'right' : 'left' }),
};

function formatDate(d) {
    return new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
}

// ─── New Ticket Form ──────────────────────────────────────────────────────────
function NewTicketForm({ orders, onCreated, onCancel }) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [orderId, setOrderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await api.post('/api/tickets', { subject, message, order_id: orderId || undefined });
            onCreated();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create ticket');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:'24px 28px', marginBottom:24 }}>
            <h3 style={{ fontSize:16, fontWeight:700, color:'#1a1d23', marginBottom:4 }}>New Support Ticket</h3>
            <p style={{ color:'#64748b', fontSize:13, marginBottom:16 }}>Describe your issue and we'll get back to you shortly.</p>
            <form onSubmit={submit}>
                <label style={s.lbl}>Subject *</label>
                <input style={s.inp} value={subject} onChange={e=>setSubject(e.target.value)} placeholder="e.g. Order not delivered" maxLength={200} required />

                <label style={s.lbl}>Related Order (optional)</label>
                <select style={s.inp} value={orderId} onChange={e=>setOrderId(e.target.value)}>
                    <option value="">— No specific order —</option>
                    {orders.map(o => (
                        <option key={o.id} value={o.id}>#{o.id} — {o.service_name} ({o.status})</option>
                    ))}
                </select>

                <label style={s.lbl}>Message *</label>
                <textarea style={{...s.inp, minHeight:100, resize:'vertical'}} value={message} onChange={e=>setMessage(e.target.value)} placeholder="Explain your issue in detail..." required />

                {error && <div style={s.err}>{error}</div>}

                <div style={{ display:'flex', gap:10, marginTop:18 }}>
                    <button type="submit" style={s.btn} disabled={loading}>{loading ? 'Sending...' : 'Submit Ticket'}</button>
                    <button type="button" onClick={onCancel} style={{...s.btn, background:'#f3f4f6', color:'#374151'}}>Cancel</button>
                </div>
            </form>
        </div>
    );
}

// ─── Ticket Detail ────────────────────────────────────────────────────────────
function TicketDetail({ ticketId, onBack }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/api/tickets/${ticketId}`);
            setData(res.data);
        } catch {}
        setLoading(false);
    }, [ticketId]);

    useEffect(() => { load(); }, [load]);

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            await api.post(`/api/tickets/${ticketId}/reply`, { message: reply.trim() });
            setReply('');
            load();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div style={{ color:'#64748b', padding:20 }}>Loading...</div>;
    if (!data || !data.ticket) return <div style={{ color:'#ef4444', padding:20 }}>Ticket not found.</div>;

    const { ticket, messages } = data;
    const st = STATUS_COLOR[ticket.status] || STATUS_COLOR.open;
    const canReply = ticket.status !== 'closed';

    return (
        <div>
            <button onClick={onBack} style={{ background:'none', border:'none', color:'#1d4ed8', cursor:'pointer', fontWeight:600, fontSize:14, marginBottom:16, padding:0 }}>
                ← Back to tickets
            </button>

            <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:'20px 24px', marginBottom:16 }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                    <div>
                        <div style={{ fontSize:18, fontWeight:700, color:'#1a1d23', marginBottom:4 }}>{ticket.subject}</div>
                        <div style={{ fontSize:12, color:'#94a3b8' }}>Ticket #{ticket.id} · Opened {formatDate(ticket.created_at)}</div>
                    </div>
                    <span style={s.badge(ticket.status)}>{st.label}</span>
                </div>
                {ticket.order_id && (
                    <div style={{ marginTop:12, padding:'10px 14px', background:'#f8fafc', borderRadius:8, fontSize:13, color:'#475569' }}>
                        <strong>Related order:</strong> #{ticket.order_id} — {ticket.order_link} ({ticket.order_status})
                    </div>
                )}
            </div>

            {/* Messages */}
            <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:'20px 24px' }}>
                <div style={s.msgWrap}>
                    {messages.map(m => (
                        <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems: m.is_admin ? 'flex-end' : 'flex-start' }}>
                            <div style={s.msg(m.is_admin)}>{m.message}</div>
                            <div style={s.msgMeta(m.is_admin)}>
                                {m.is_admin ? 'OrbitGrowth Staff' : m.sender_username} · {formatDate(m.created_at)}
                            </div>
                        </div>
                    ))}
                </div>

                {canReply && (
                    <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:16 }}>
                        <textarea
                            value={reply}
                            onChange={e => setReply(e.target.value)}
                            placeholder="Type your reply..."
                            style={{...s.inp, minHeight:80, resize:'vertical', marginBottom:10}}
                        />
                        <button style={s.btnSm} onClick={sendReply} disabled={sending || !reply.trim()}>
                            {sending ? 'Sending...' : 'Send Reply'}
                        </button>
                    </div>
                )}
                {ticket.status === 'closed' && (
                    <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:16, color:'#94a3b8', fontSize:13, textAlign:'center', fontWeight:600 }}>
                        This ticket is closed. Please open a new ticket if you need further help.
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Main Tickets Page ────────────────────────────────────────────────────────
export default function Tickets() {
    const [tickets, setTickets] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const loadTickets = useCallback(async () => {
        try {
            const res = await api.get('/api/tickets?limit=50&own=true');
            setTickets(res.data.tickets || []);
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => {
        loadTickets();
        // Load recent orders for the dropdown
        api.get('/auth/orders?limit=20').then(r => setOrders(r.data || [])).catch(() => {});
    }, [loadTickets]);

    if (selectedId) return <TicketDetail ticketId={selectedId} onBack={() => { setSelectedId(null); loadTickets(); }} />;

    return (
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
                <div>
                    <h1 style={s.h1}>Support Tickets</h1>
                    <p style={s.sub}>Having an issue? Open a ticket and we'll help you out.</p>
                </div>
                {!showNew && (
                    <button style={s.btn} onClick={() => setShowNew(true)}>+ New Ticket</button>
                )}
            </div>

            {showNew && (
                <NewTicketForm
                    orders={orders}
                    onCreated={() => { setShowNew(false); loadTickets(); }}
                    onCancel={() => setShowNew(false)}
                />
            )}

            {loading ? (
                <div style={{ color:'#64748b', textAlign:'center', padding:40 }}>Loading...</div>
            ) : tickets.length === 0 ? (
                <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>🎫</div>
                    <div style={{ fontSize:16, fontWeight:600, marginBottom:6 }}>No tickets yet</div>
                    <div style={{ fontSize:14 }}>Open your first ticket if you need help.</div>
                </div>
            ) : (
                tickets.map(t => (
                    <div key={t.id} style={s.card} onClick={() => setSelectedId(t.id)}
                        onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                            <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontWeight:700, fontSize:15, color:'#1a1d23', marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                    #{t.id} — {t.subject}
                                </div>
                                <div style={{ fontSize:12, color:'#94a3b8' }}>
                                    {formatDate(t.created_at)} · {t.message_count} message{t.message_count !== '1' ? 's' : ''}
                                    {t.order_id && ` · Order #${t.order_id}`}
                                </div>
                                {t.last_message && (
                                    <div style={{ fontSize:13, color:'#64748b', marginTop:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                        {t.last_message}
                                    </div>
                                )}
                            </div>
                            <span style={s.badge(t.status)}>{STATUS_COLOR[t.status]?.label || t.status}</span>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}
